import type { TFunction } from 'i18next';

import type { ActionItem } from '@/components/actions-button/ActionsButton';

import type { ChartExportFormat } from './exportChart';

type BuildResultActionItemsParams = {
  t: TFunction<'common', 'indicator_results'>;
  canAddResult: boolean;
  canDeleteResult: boolean;
  canExport: boolean;
  hasSelectedResults: boolean;
  /** Whether the chart currently has results to export (CUS-145). */
  hasResultsInRange: boolean;
  onAddResult: () => void;
  onDeleteResult: () => void;
  onExport: (format: ChartExportFormat) => void;
};

/**
 * The export formats offered in the Actions dropdown, in menu order — PNG,
 * JPEG, then SVG, matching the dashboard line-chart widget's export options
 * (CUS-145).
 */
const EXPORT_FORMATS: {
  format: ChartExportFormat;
  id: string;
  labelKey: 'actions.export_jpeg' | 'actions.export_png' | 'actions.export_svg';
}[] = [
  { format: 'png', id: 'exportPng', labelKey: 'actions.export_png' },
  { format: 'jpeg', id: 'exportJpeg', labelKey: 'actions.export_jpeg' },
  { format: 'svg', id: 'exportSvg', labelKey: 'actions.export_svg' },
];

/**
 * Builds the ordered Add / Delete / Export items for the indicator results
 * Actions dropdown. Export offers PNG / JPEG / SVG as three flat items, mirroring
 * the dashboard line-chart widget's export menu (CUS-145).
 *
 * Permission-gated actions are HIDDEN when the user lacks the permission —
 * matching the platform convention for dropdown actions (cf. the controls
 * Actions dropdown), rather than rendering them disabled. Disabled states are
 * contextual: Delete with no selection, and each Export format with no results
 * on the chart (empty state, or a non-numeric indicator with no chart) — the
 * user has the permission, so the item stays visible and disabled with a
 * tooltip.
 */
export const buildResultActionItems = ({
  t,
  canAddResult,
  canDeleteResult,
  canExport,
  hasSelectedResults,
  hasResultsInRange,
  onAddResult,
  onDeleteResult,
  onExport,
}: BuildResultActionItemsParams): ActionItem[] => {
  const items: ActionItem[] = [];

  if (canAddResult) {
    items.push({
      id: 'addResult',
      text: t('create_new_button'),
      onItemClick: onAddResult,
    });
  }

  if (canDeleteResult) {
    items.push({
      id: 'deleteResult',
      text: t('delete_button'),
      disabled: !hasSelectedResults,
      disabledReason: hasSelectedResults
        ? undefined
        : t('actions.delete_disabled_no_selection'),
      onItemClick: onDeleteResult,
    });
  }

  if (canExport) {
    for (const { format, id, labelKey } of EXPORT_FORMATS) {
      items.push({
        id,
        text: t(labelKey),
        disabled: !hasResultsInRange,
        disabledReason: hasResultsInRange
          ? undefined
          : t('actions.export_disabled_no_results'),
        onItemClick: () => onExport(format),
      });
    }
  }

  return items;
};
